import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Ticket, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../context/AuthContext";
import { useLeague } from "../../context/LeagueContext";
import { fmtDate, fmtOdds, fmtTokens } from "../../lib/format";
import CouponReceipt from "../../components/CouponReceipt";

const statusBadge = (status) => {
  if (status === "won") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "lost") return "bg-red-50 text-red-700 border-red-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
};
const statusLabel = (s) => (s === "won" ? "Vunnen" : s === "lost" ? "Förlorad" : "Aktiv");

export default function Players() {
  const { currentLeague } = useLeague();
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [playerCoupons, setPlayerCoupons] = useState([]);
  const [receipt, setReceipt] = useState(null);

  const load = () => {
    if (currentLeague) {
      api.get(`/leagues/${currentLeague.id}/players`).then((r) => setPlayers(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, [currentLeague]);

  const openPlayer = async (p) => {
    setSelected(p);
    const r = await api.get(`/leagues/${currentLeague.id}/coupons?player_id=${p.id}`).catch(() => ({ data: [] }));
    setPlayerCoupons(r.data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/leagues/${currentLeague.id}/players`, { name });
      toast.success(`${name.trim()} tillagd i ${currentLeague.name}`);
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Kunde inte lägga till spelaren"));
    } finally {
      setLoading(false);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name}

  const remove = async (id, n) => {
    await api.delete(`/leagues/${currentLeague.id}/players/${id}`).catch(() => {});
    toast.success(`${n} borttagen`);
    setDeleteConfirm(null);
    load();
  };

  return (
    <div data-testid="admin-players">
      <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">Spelare</h1>
      <p className="text-sm text-slate-500 mb-10">Spelarlistan i {currentLeague?.name}. Spelarna behöver inga egna konton.</p>

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 mb-10 flex flex-col sm:flex-row gap-4 sm:items-end"
        data-testid="add-player-form"
      >
        <div className="flex-1">
          <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 block mb-2">Nytt namn</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="T.ex. Lisa"
            data-testid="add-player-input"
            className="w-full bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="add-player-submit-button"
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
        >
          <Plus size={16} /> {loading ? "Lägger till..." : "Lägg till spelare"}
        </button>
      </motion.form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {players.length === 0 && <p className="text-sm text-slate-500" data-testid="players-empty">Inga spelare ännu i ligan.</p>}
        {players.map((p, i) => (
          <motion.button
            key={p.id}
            type="button"
            onClick={() => openPlayer(p)}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="group bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between text-left hover:border-blue-300 hover:shadow-md hover:shadow-slate-900/5 transition-shadow"
            data-testid={`player-card-${p.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm">
                {p.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-display font-semibold">{p.name}</p>
                <p className="text-[11px] text-slate-400 group-hover:text-blue-600 transition-colors">Visa kuponger →</p>
              </div>
            </div>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ id: p.id, name: p.name }); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setDeleteConfirm({ id: p.id, name: p.name }); } }}
              data-testid={`delete-player-button-${p.id}`}
              className="text-slate-300 hover:text-red-600 transition-colors p-1"
            >
              <Trash2 size={16} />
            </span>
          </motion.button>
        ))}
      </div>

      {/* Spelarkuponger */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            data-testid="player-detail-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-3xl shadow-2xl max-h-[85vh] flex flex-col"
              data-testid="player-detail"
            >
              <div className="px-7 py-6 border-b border-slate-100 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold text-lg shrink-0">
                  {selected.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-display font-bold text-xl" data-testid="player-detail-name">{selected.name}</p>
                  <p className="text-[11px] text-slate-500">{playerCoupons.length} kuponger totalt</p>
                </div>
                <button onClick={() => setSelected(null)} data-testid="player-detail-close" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors">
                  <ChevronLeft size={16} /> Stäng
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-3">
                {playerCoupons.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-8" data-testid="player-coupons-empty">Inga kuponger lagda ännu.</p>
                )}
                {playerCoupons.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setReceipt(c)}
                    className="w-full text-left bg-[#FAFAFC] border border-slate-200 rounded-2xl p-4 hover:border-blue-300 hover:shadow-sm transition-shadow"
                    data-testid={`player-coupon-${c.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-blue-600">
                        <Ticket size={15} />
                        <span className="font-mono2 text-xs font-semibold">#{c.id.slice(-8).toUpperCase()}</span>
                      </div>
                      <span className={`text-[11px] font-semibold border px-2.5 py-0.5 rounded-full ${statusBadge(c.status)}`}>
                        {statusLabel(c.status)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-1.5">
                      {c.legs.map((l) => `${l.home_team}–${l.away_team} (${l.pick})`).join(", ")}
                    </p>
                    <div className="flex gap-5 text-xs text-slate-500">
                      <span>Lagd {fmtDate(c.created_at)}</span>
                      <span className="font-mono2">Insats {fmtTokens(c.stake)}</span>
                      <span className="font-mono2 font-semibold">{fmtOdds(c.total_odds)}x</span>
                      <span className={`font-mono2 font-semibold ${c.status === "won" ? "text-emerald-600" : c.status === "lost" ? "text-red-500" : "text-blue-600"}`}>
                        {fmtTokens(c.status === "active" ? c.potential_return : c.payout)} tokens
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CouponReceipt coupon={receipt} onClose={() => setReceipt(null)} />

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
            data-testid="delete-confirm-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-3xl p-7 sm:p-8 shadow-2xl"
              data-testid="delete-confirm-dialog"
            >
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-4">Ta bort spelare</p>
              <p className="text-sm text-slate-900 mb-2">
                Är du säker på att du vill ta bort <strong>{deleteConfirm.name}</strong>?
              </p>
              <p className="text-xs text-slate-600 mb-6">
                Spelarens historik och resultat kommer att sparas, men spelaren kan inte lägga fler kuponger.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-5 py-3 rounded-xl transition-colors border border-slate-200"
                  data-testid="delete-cancel-button"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => remove(deleteConfirm.id, deleteConfirm.name)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
                  data-testid="delete-confirm-button"
                >
                  Ta bort
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
